// Холст редактора: линейка, листы перед текстом (титул и др. из registry.sheets), лист A4 с документом (TipTap),
// границы страниц, ручка блоков, поиск, мини-панель выделения, «/»-меню. Масштаб - Ctrl+колесо.
// Контекстное меню - из registry.contextMenu (правый щелчок; Shift+правый - меню браузера).
import { useEffect, useRef, useState } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import { TextSelection } from '@tiptap/pm/state';
import { buildExtensions } from '../core/editor/extensions';
import { attachEditor } from '../core/project';
import { appStore, useApp } from '../core/store';
import { ctx } from '../core/ctx';
import { registry, type ContextTarget, type MenuItemSpec } from '../core/registry';
import { useRegistry } from './hooks';
import { MenuList, Popover } from './controls';
import { uiStore, useUi } from './uiState';
import { FindBar } from '../features/shell/find';
import { BubbleBar } from '../features/shell/BubbleBar';
import { SlashMenu } from '../features/shell/slash';
import { BlockHandle } from '../features/shell/BlockHandle';
import { usePagination } from '../features/shell/pagination';
import { PagesLayer, PagesOverlay, FrontSheetFrame } from '../features/pages/PagesLayer';
import { fitWidthZoom, setZoom, ZOOM_MAX, ZOOM_MIN } from '../features/shell/commands';

/** Линейка: поля по СТО (25 / 15 мм) затенены, отметки через 5 мм, абзацный отступ 1,25 см. */
function Ruler() {
  const ticks: React.ReactNode[] = [];
  for (let mm = 0; mm <= 210; mm += 5) {
    const fromMargin = mm - 25;
    const cm = fromMargin % 10 === 0;
    ticks.push(<span key={mm} className={'ruler-tick' + (cm ? ' is-cm' : '')} style={{ left: mm + 'mm' }}>
      {cm && fromMargin > 0 && fromMargin < 170 ? <i>{fromMargin / 10}</i> : null}
    </span>);
  }
  return (
    <div className="ruler" aria-hidden data-tip="Поля и отступы задаёт СТО" data-tip-desc="Левое поле 25 мм, правое 15 мм, абзацный отступ 1,25 см">
      <div className="ruler-margin" style={{ left: 0, width: '25mm' }} />
      <div className="ruler-margin" style={{ right: 0, width: '15mm' }} />
      {ticks}
      <span className="ruler-indent" style={{ left: '37.5mm' }} />
      <span className="ruler-left" style={{ left: '25mm' }} />
    </div>
  );
}

export function EditorPage() {
  const initialDoc = useApp((s) => s.initialDoc);
  const projectId = useApp((s) => s.projectId);
  const zoom = useApp((s) => s.zoom);
  const showRuler = useUi((s) => s.showRuler);
  const showPages = useUi((s) => s.showPages);
  const focus = useUi((s) => s.focusMode);
  const sheets = useRegistry(registry.sheets);
  useApp((s) => s.meta);   // листы зависят от метаданных
  const editor = useEditor({
    extensions: buildExtensions(),
    content: initialDoc || undefined,
    immediatelyRender: true,
    shouldRerenderOnTransaction: false,
    editorProps: { attributes: { class: 'sto-doc', spellcheck: 'true', lang: 'ru', 'data-testid': 'editor', 'aria-label': 'Текст отчёта', role: 'textbox', 'aria-multiline': 'true' } },
    onCreate: ({ editor: ed }) => attachEditor(ed),
  }, [projectId]);
  useEffect(() => () => { if (appStore.getState().editor === editor) appStore.getState().set({ editor: null }); }, [editor]);

  const canvasRef = useRef<HTMLDivElement>(null);
  const sheetRef = useRef<HTMLDivElement>(null);
  usePagination(sheetRef);

  // --- масштаб: Ctrl+колесо (точка под мышью остаётся на месте), «по ширине» при изменении окна
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const wheel = (e: WheelEvent) => {
      if (!e.ctrlKey) return;
      e.preventDefault();
      const old = appStore.getState().zoom;
      const nz = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, old * (e.deltaY < 0 ? 1.1 : 1 / 1.1)));
      const r = c.getBoundingClientRect();
      const px = e.clientX - r.left; const py = e.clientY - r.top;
      const ax = (c.scrollLeft + px) / old; const ay = (c.scrollTop + py) / old;
      setZoom(Math.abs(nz - 1) < 0.03 ? 1 : nz);
      requestAnimationFrame(() => {
        const z = appStore.getState().zoom;
        c.scrollLeft = ax * z - px; c.scrollTop = ay * z - py;
      });
    };
    c.addEventListener('wheel', wheel, { passive: false });
    // узкое окно при открытии: лист целиком по ширине (дальше масштаб - как выберет пользователь)
    requestAnimationFrame(() => {
      const z = fitWidthZoom();
      if (z && uiStore.getState().zoomMode === 'manual' && z < appStore.getState().zoom - 0.02 && z < 1) setZoom(z, 'fit');
    });
    // ручной масштаб: если лист перестал помещаться (открыли панели «Структура» и «Проверка»), масштаб временно
    // уменьшается «по ширине», а когда место вернулось - восстанавливается выбранный пользователем
    let userZoom: number | null = null;
    let autoZoom: number | null = null;
    const ro = new ResizeObserver(() => {
      const z = fitWidthZoom();
      if (!z) return;
      const cur = appStore.getState().zoom;
      if (uiStore.getState().zoomMode === 'fit') { if (Math.abs(z - cur) > 0.005) setZoom(z, 'fit'); return; }
      if (autoZoom !== null && Math.abs(cur - autoZoom) > 0.005) { userZoom = null; autoZoom = null; }   // пользователь сменил масштаб сам
      if (z < cur - 0.02) {
        if (userZoom === null) userZoom = cur;
        autoZoom = Math.round(z * 100) / 100;
        setZoom(z);
      } else if (userZoom !== null && z > cur + 0.02) {
        const nz = Math.min(userZoom, z);
        autoZoom = nz === userZoom ? null : Math.round(nz * 100) / 100;
        setZoom(nz);
        if (autoZoom === null) userZoom = null;
      }
    });
    ro.observe(c);
    return () => { c.removeEventListener('wheel', wheel); ro.disconnect(); };
  }, []);

  // --- контекстное меню
  const [menu, setMenu] = useState<{ x: number; y: number; items: MenuItemSpec[] } | null>(null);
  const onContext = (e: React.MouseEvent) => {
    if (!editor || e.shiftKey) return;                 // Shift + правая кнопка - обычное меню браузера
    const view = editor.view;
    const found = view.posAtCoords({ left: e.clientX, top: e.clientY });
    const pos = found ? found.pos : null;
    // щелчок вне выделения - курсор туда (как в Word)
    if (pos !== null) {
      const sel = editor.state.selection;
      if (pos < sel.from || pos > sel.to) {
        try { view.dispatch(editor.state.tr.setSelection(TextSelection.near(editor.state.doc.resolve(pos)))); } catch { /* атом */ }
      }
    }
    const nodes: ContextTarget['nodes'] = [];
    if (pos !== null) {
      const $p = editor.state.doc.resolve(pos);
      for (let d = 1; d <= $p.depth; d++) nodes.push({ type: $p.node(d).type.name, attrs: $p.node(d).attrs, pos: $p.before(d) });
      const after = $p.nodeAfter;
      if (found && found.inside >= 0) {
        const n = editor.state.doc.nodeAt(found.inside);
        if (n && n.isAtom) nodes.push({ type: n.type.name, attrs: n.attrs, pos: found.inside });
      } else if (after && after.isAtom) nodes.push({ type: after.type.name, attrs: after.attrs, pos });
    }
    const target: ContextTarget = { pos, nodes, event: e.nativeEvent, inTable: nodes.some((n) => n.type === 'table'),
      selectionEmpty: editor.state.selection.empty };
    const groups = registry.contextMenu.list().map((p) => { try { return p.items(ctx, target) || []; } catch (err) { console.error(err); return []; } })
      .filter((g) => g.length);
    if (!groups.length) return;
    e.preventDefault();
    const items: MenuItemSpec[] = [];
    groups.forEach((g, i) => { if (i) items.push({ separator: true }); items.push(...g); });
    setMenu({ x: e.clientX, y: e.clientY, items });
  };

  return (
    <div className="canvas-wrap">
      <FindBar />
      <div className="canvas" data-testid="canvas" ref={canvasRef}>
        <div className="canvas-inner" style={{ zoom }}>
          {showRuler && !focus && <Ruler />}
          {!focus && sheets.filter((s) => !s.visible || s.visible(ctx)).map((s) => (
            showPages && s.id !== 'front'
              ? <FrontSheetFrame key={s.id} id={s.id} order={s.order ?? 100}><s.component ctx={ctx} /></FrontSheetFrame>
              : <div key={s.id} className="sheet sheet-front" data-sheet={s.id}><s.component ctx={ctx} /></div>
          ))}
          <div className={'sheet sheet-main' + (showPages ? ' show-pages pages-mode' : '')} ref={sheetRef} onContextMenu={onContext}>
            {showPages && <PagesLayer />}
            <div className="pg-flow"><EditorContent editor={editor} /></div>
            {showPages && <PagesOverlay />}
            <BlockHandle sheet={sheetRef} />
          </div>
        </div>
      </div>
      <BubbleBar />
      <SlashMenu />
      {menu && (
        <Popover point={{ x: menu.x, y: menu.y }} onClose={() => setMenu(null)}>
          <MenuList items={menu.items} onDone={() => setMenu(null)} />
        </Popover>
      )}
    </div>
  );
}
